import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import DashboardLayout from "../../layout/DashboardLayout/DashboardLayout";
import {
  Table,
  Input,
  Button,
  DatePicker,
  Space,
  Tag,
  Avatar,
  ConfigProvider,
  Row,
  Col,
  Card,
  theme,
  Typography,
  Spin,
  Alert,
  Modal,
  Radio,
  message,
  Select,
  Dropdown,
  Menu,
} from "antd";
import {
  SearchOutlined,
  DownloadOutlined,
  FilterOutlined,
  FileImageOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import services from "../../services";
import emptyStateImage from "../../assets/images/Frame 1171275820.png";
import successCheckIcon from "../../assets/icons/success_check.png";
import errorExclamationIcon from "../../assets/icons/error_exclamation.png";
import "./styles.css";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const parseTripSearchInput = (text) => {
  const trimmed = (text || "").trim();
  if (!trimmed) return {};

  const tripIdMatch = trimmed.match(/^#?T(\d+)$/i);
  if (tripIdMatch) {
    return { tripId: tripIdMatch[1] };
  }

  if (/^\d+$/.test(trimmed)) {
    return { tripId: trimmed };
  }

  return { username: trimmed };
};

const TripVerification = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // Modal states
  const [approveModalVisible, setApproveModalVisible] = useState(false);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [successModalVisible, setSuccessModalVisible] = useState(false);
  const [filterDropdownVisible, setFilterDropdownVisible] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [actionType, setActionType] = useState(null); // 'approve' or 'reject'
  const [actionCount, setActionCount] = useState(0);
  const [processingAction, setProcessingAction] = useState(false);

  // Proof preview modal states
  const [proofModalVisible, setProofModalVisible] = useState(false);
  const [proofUrls, setProofUrls] = useState([]);
  const [proofIndex, setProofIndex] = useState(0);

  // Reject reason states
  const [rejectReason, setRejectReason] = useState("");
  const [otherReasonText, setOtherReasonText] = useState("");
  const [searchText, setSearchText] = useState("");
  const [dateRange, setDateRange] = useState(null);

  // Filter states
  const [filterUserName, setFilterUserName] = useState("");
  const [filterTripId, setFilterTripId] = useState("");
  const [filterTripStatus, setFilterTripStatus] = useState("");
  const [filterMode, setFilterMode] = useState("");
  const [filterDateRange, setFilterDateRange] = useState(null);
  const [appliedFilters, setAppliedFilters] = useState({});

  const token = useSelector((state) => state.auth.token);

  // Fetch trips data from API
  useEffect(() => {
    fetchTrips(1, pagination.pageSize); // Reset to page 1 when filters change
  }, [appliedFilters, searchText]); // Add searchText dependency

  const fetchTrips = async (pageNo = 1, pageSize = 10) => {
    setLoading(true);
    setError(null);
    try {
      // UI status labels shown in the table/filter modal.
      // We map those labels to API `status` codes, and then (when needed) do a small
      // post-filter to split `Approved` vs `Auto Approved`.
      const uiTripStatus = appliedFilters?.status || "";
      const mapUiStatusToApiStatus = (uiStatus) => {
        switch (uiStatus) {
          case "Pending":
            // Pending trips are not verified yet.
            return "NOT_VERIFIED";
          case "Rejected":
            // Admin reject sets status = FAILED.
            return "FAILED";
          case "In Progress":
            return "IN_PROGRESS";
          case "Approved":
            // Backend may return both Approved and Auto Approved; we split client-side.
            return "VERIFIED";
          case "Auto Approved":
            return "VERIFIED";
          default:
            return undefined;
        }
      };

      const filters = {
        ...parseTripSearchInput(searchText),
        ...appliedFilters,
      };

      // Convert date range to Unix timestamps (ms) for backend
      if (
        appliedFilters.dateRange &&
        appliedFilters.dateRange[0] &&
        appliedFilters.dateRange[1]
      ) {
        filters.startsAt = appliedFilters.dateRange[0].startOf("day").unix();
        filters.endsAt = appliedFilters.dateRange[1].endOf("day").unix();
        delete filters.dateRange;
      }

      // appliedFilters override search bar when the same key is set in the filter modal
      if (!filters.username) delete filters.username;
      if (!filters.tripId) delete filters.tripId;

      // Translate the UI status label into what the backend expects.
      if (filters.status) {
        filters.status = mapUiStatusToApiStatus(filters.status);
      }

      const response = await services.getTrips(pageNo, pageSize, filters);
      console.log("API Response:", response.data); // Debug log
      if (response?.data?.statusCode === 200) {
        const rawTripsData = response.data.data.trips || [];
        // Filter out trips where userFirstName, userLastName, and userEmail are all null
        const tripsData = rawTripsData.filter(
          (trip) => trip.userFirstName !== null || trip.userLastName !== null || trip.userEmail !== null
        );

        console.log(
          "Trips data:",
          tripsData.length,
          "Total count:",
          response.data.data.totalCount,
        ); // Debug log

        const formattedData = tripsData.map((trip) => {
          // Calculate total CO2 offset from all travel modes
          const totalCO2Offset =
            trip.travelModes?.reduce(
              (sum, mode) => sum + (mode.co2eOffset || 0),
              0,
            ) || 0;

          // Determine travel mode - show all modes
          let travelMode = "N/A";
          if (trip.travelModes && trip.travelModes.length > 0) {
            const modes = trip.travelModes.map((mode) => {
              const modeName = mode.modeName || "";
              return modeName.charAt(0).toUpperCase() + modeName.slice(1);
            });
            travelMode = modes.join(", ");
          }

          // Collect ALL ticket links from all travel modes (multimodal support)
          const ticketLinks =
            trip.travelModes
              ?.flatMap((mode) => {
                if (!mode.ticketLink) return [];
                const name = mode.modeName || "";
                return [{ url: mode.ticketLink, modeName: name.charAt(0).toUpperCase() + name.slice(1) }];
              }) || [];

          // Map status - Handle all API status values
          let displayStatus = "Pending";
          if (trip.status === "IN_PROGRESS") {
            displayStatus = "In Progress";
          } else if (trip.status === "COMPLETED") {
            if (trip.verifiedAt) {
              displayStatus = trip.autoVerified
                ? "Auto Approved"
                : "Approved";
            } else {
              displayStatus = "Pending";
            }
          } else if (trip.status === "VERIFIED") {
            displayStatus = trip.autoVerified ? "Auto Approved" : "Approved";
          } else if (trip.status === "NOT_VERIFIED") {
            displayStatus = "Pending";
          } else if (trip.status === "REJECTED" || trip.status === "FAILED") {
            displayStatus = "Rejected";
          }

          const hasName = trip.userFirstName && trip.userLastName;

          return {
            key: trip.id,
            userName: hasName
              ? `${trip.userFirstName} ${trip.userLastName}`
              : trip.userEmail || "Unknown User",
            email: trip.userEmail || "N/A",
            avatar: trip.userProfileImage || null,
            avatarInitials: hasName
              ? `${trip.userFirstName[0]}${trip.userLastName[0]}`
              : null,
            tripId: `#T${trip.id}`,
            distance: trip.distance ? `${trip.distance} miles` : "N/A",
            mode: travelMode,
            co2Saved:
              totalCO2Offset > 0 ? `${totalCO2Offset.toFixed(2)} kg` : "0 kg",
            ticket: ticketLinks.length > 0 ? ticketLinks[0].url : null, // primary for display
            tickets: ticketLinks, // all proofs for modal viewer ({ url, modeName })
            status: displayStatus,
            statusColor: getStatusColor(displayStatus),
            createdAt: trip.createdAt,
          };
        });

        // If the backend status filter is too coarse for UI purposes (e.g. VERIFIED includes
        // both Approved and Auto Approved), refine the results client-side for the selected UI label.
        const filteredByUiStatus = uiTripStatus
          ? formattedData.filter((trip) => trip.status === uiTripStatus)
          : formattedData;

        setData(filteredByUiStatus);

        setPagination({
          current: response.data.data.currentPage || pageNo,
          pageSize: response.data.data.pageSize || pageSize,
          total: response.data.data.totalCount || 0,
        });
      }
    } catch (err) {
      console.error("Error fetching trips:", err);
      setError("Failed to load trip verification data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    if (!status) return "orange";
    const statusLower = status.toLowerCase();
    if (statusLower.includes("approved")) return "green";
    if (statusLower.includes("rejected")) return "red";
    if (
      statusLower.includes("pending") ||
      statusLower.includes("queue") ||
      statusLower.includes("progress")
    )
      return "orange";
    return "default";
  };

  const columns = [
    {
      title: "User Name",
      dataIndex: "userName",
      key: "userName",
      width: 250,
      render: (text, record) => (
        <Space>
          {record.avatarInitials ? (
            <Avatar
              style={{
                backgroundColor: "#D3F261",
                color: "#181D27",
                fontWeight: 600,
              }}
            >
              {record.avatarInitials}
            </Avatar>
          ) : record.avatar ? (
            <Avatar src={record.avatar} />
          ) : (
            <Avatar src={`https://i.pravatar.cc/150?u=${record.key}`} />
          )}
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Text strong>{text}</Text>
            <Text type="secondary" style={{ fontSize: "12px" }}>
              {record.email}
            </Text>
          </div>
        </Space>
      ),
    },
    {
      title: "Trip ID",
      dataIndex: "tripId",
      key: "tripId",
      width: 100,
      render: (text) => <Text type="secondary">{text}</Text>,
    },
    {
      title: "Distance",
      dataIndex: "distance",
      key: "distance",
      width: 150,
      render: (text) => <Text type="secondary">{text}</Text>,
    },
    {
      title: "Mode",
      dataIndex: "mode",
      key: "mode",
      width: 150,
      render: (text) => <Text type="secondary">{text}</Text>,
    },
    {
      title: "Co2 Saved",
      dataIndex: "co2Saved",
      key: "co2Saved",
      width: 100,
      render: (text) => <Text type="secondary">{text}</Text>,
    },
    {
      title: "Uploaded Ticket / Proof",
      dataIndex: "ticket",
      key: "ticket",
      width: 180,
      render: (text, record) => {
        const allTickets = record.tickets || (text ? [{ url: text, modeName: "" }] : []);
        if (allTickets.length === 0) {
          return <Text type="secondary">No proof uploaded</Text>;
        }
        const count = allTickets.length;
        const fileName = allTickets[0].url.split("/").pop() || "Ticket";
        const maxLength = 10;
        const trimmedFileName =
          fileName.length > maxLength
            ? fileName.substring(0, maxLength) + "..."
            : fileName;
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              border: "1px solid #EAECF0",
              padding: "8px 12px",
              borderRadius: "8px",
              cursor: "pointer",
              maxWidth: "180px",
            }}
            onClick={() => {
              setProofUrls(allTickets);
              setProofIndex(0);
              setProofModalVisible(true);
            }}
            title={`View ${count} proof${count > 1 ? 's' : ''}`}
          >
            <FileImageOutlined style={{ color: "#004EEB", flexShrink: 0 }} />
            <span
              style={{
                color: "#344054",
                fontWeight: 600,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display: "inline-block",
              }}
            >
              {trimmedFileName}
            </span>
            {count > 1 && (
              <span
                style={{
                  background: "#5FB92A",
                  color: "#fff",
                  borderRadius: "10px",
                  fontSize: "11px",
                  fontWeight: 700,
                  padding: "1px 6px",
                  flexShrink: 0,
                }}
              >
                {count}
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 150,
      render: (text, record) => {
        let color = "default";
        let bgcolor = "#F2F4F7";
        let textcolor = "#344054";

        if (record.status.includes("Approved")) {
          bgcolor = "#ECFDF3";
          textcolor = "#027A48";
        } else if (
          record.status === "Pending" ||
          record.status === "In Progress"
        ) {
          bgcolor = "#FFFAEB";
          textcolor = "#B54708";
        } else if (record.status === "Rejected") {
          bgcolor = "#FEF3F2";
          textcolor = "#B42318";
        }

        return (
          <Tag
            style={{
              borderRadius: "16px",
              color: textcolor,
              backgroundColor: bgcolor,
              border: "none",
              padding: "2px 10px",
              fontWeight: 500,
            }}
          >
            {text}
          </Tag>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 200,
      render: (_, record) => {
        const hasDocuments = record.tickets?.length > 0;
        const isDisabled =
          !hasDocuments ||
          (record.status !== "Pending" && record.status !== "In Progress");

        return (
          <Space>
            <Button
              danger
              className="action-btn-reject"
              onClick={() => handleReject(record)}
              disabled={isDisabled}
            >
              Reject
            </Button>
            <Button
              type="primary"
              className="action-btn-approve"
              onClick={() => handleApprove(record)}
              disabled={isDisabled}
            >
              Approve
            </Button>
          </Space>
        );
      },
    },
  ];

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const handleExport = () => {
    // Define CSV headers
    const headers = [
      "User Name",
      "Email",
      "Trip ID",
      "Distance",
      "Mode",
      "Co2 Saved",
      "Ticket",
      "Status",
    ];

    // Convert data to CSV rows
    const csvContent = [
      headers.join(","),
      ...data.map((row) =>
        [
          `"${row.userName}"`,
          `"${row.email}"`,
          `"${row.tripId}"`,
          `"${row.distance}"`,
          `"${row.mode}"`,
          `"${row.co2Saved}"`,
          `"${row.ticket}"`,
          `"${row.status}"`,
        ].join(","),
      ),
    ].join("\n");

    // Create blob and download link
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.setAttribute("download", "trip_verification_data.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);

    link.click();

    // Cleanup with a small delay to ensure download starts
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 100);
  };

  // Handle approve button click (single or bulk)
  const handleApprove = (trip = null) => {
    if (trip) {
      // Single trip approval
      setSelectedTrip(trip);
    } else {
      // Bulk approval
      setSelectedTrip(null);
    }
    setApproveModalVisible(true);
  };

  // Handle reject button click (single or bulk)
  const handleReject = (trip = null) => {
    if (trip) {
      // Single trip rejection
      setSelectedTrip(trip);
    } else {
      // Bulk rejection
      setSelectedTrip(null);
    }
    setRejectReason("");
    setOtherReasonText("");
    setRejectModalVisible(true);
  };

  // Confirm approve action (single or bulk)
  const confirmApprove = async () => {
    setProcessingAction(true);
    try {
      const tripsToApprove = selectedTrip
        ? [selectedTrip]
        : data.filter((trip) => selectedRowKeys.includes(trip.key));

      // Process all trips in parallel
      await Promise.all(
        tripsToApprove.map((trip) =>
          services.verifyTrip(trip.key, "VERIFIED", null, null),
        ),
      );

      setApproveModalVisible(false);
      setActionType("approve");
      setActionCount(tripsToApprove.length);
      setSuccessModalVisible(true);
      setSelectedRowKeys([]);

      // Refresh data while success modal is visible (matches ManageChallenges pattern)
      fetchTrips(1, pagination.pageSize);
    } catch (err) {
      console.error("Error approving trip(s):", err);
      message.error("Failed to approve trip(s). Please try again.");
    } finally {
      setProcessingAction(false);
    }
  };

  // Confirm reject action (single or bulk)
  const confirmReject = async () => {
    if (!rejectReason) {
      message.warning("Please select a reason for rejection");
      return;
    }

    if (rejectReason === "Other" && !otherReasonText.trim()) {
      message.warning("Please enter a reason for rejection");
      return;
    }

    setProcessingAction(true);
    try {
      // Map rejection reason to API format
      let apiReason = "OTHER";
      let reasonText = rejectReason;

      if (rejectReason === "Blurry or invalid image") {
        apiReason = "INVALID_ROUTE";
        reasonText = "Blurry or invalid image uploaded";
      } else if (rejectReason === "Wrong ticket uploaded") {
        apiReason = "WRONG_TICKET";
        reasonText = "Wrong ticket uploaded for this trip";
      } else if (rejectReason === "Missing proof of travel") {
        apiReason = "MISSING_PROOF";
        reasonText = "Missing proof of travel";
      } else if (rejectReason === "Other") {
        apiReason = "OTHER";
        reasonText = otherReasonText;
      }

      const tripsToReject = selectedTrip
        ? [selectedTrip]
        : data.filter((trip) => selectedRowKeys.includes(trip.key));

      // Process all trips in parallel
      await Promise.all(
        tripsToReject.map((trip) =>
          services.verifyTrip(trip.key, "FAILED", apiReason, reasonText),
        ),
      );

      setRejectModalVisible(false);
      setActionType("reject");
      setActionCount(tripsToReject.length);
      setSuccessModalVisible(true);
      setSelectedRowKeys([]);

      // Refresh data while success modal is visible (matches ManageChallenges pattern)
      fetchTrips(1, pagination.pageSize);
    } catch (err) {
      console.error("Error rejecting trip(s):", err);
      message.error("Failed to reject trip(s). Please try again.");
    } finally {
      setProcessingAction(false);
    }
  };

  // Close success modal
  const closeSuccessModal = () => {
    setSuccessModalVisible(false);
    setSelectedTrip(null);
    setActionType(null);
    setActionCount(0);
  };

  // Apply filters
  const applyFilters = () => {
    const filters = {};
    if (filterUserName) filters.username = filterUserName;
    if (filterTripId) filters.tripId = filterTripId;
    if (filterTripStatus) filters.status = filterTripStatus;
    if (filterMode) filters.modeOfTravel = filterMode;
    if (filterDateRange) filters.dateRange = filterDateRange;

    setAppliedFilters(filters);
    setFilterDropdownVisible(false);
    message.success("Filters applied successfully");
  };

  // Clear filters
  const clearFilters = () => {
    setFilterUserName("");
    setFilterTripId("");
    setFilterTripStatus("");
    setFilterMode("");
    setFilterDateRange(null);
    setAppliedFilters({});
    message.info("Filters cleared");
  };

  // Filter dropdown content
  const renderFilterDropdown = () => (
    <div
      style={{
        padding: "20px",
        backgroundColor: "#fff",
        borderRadius: "8px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        width: "600px",
      }}
    >
      <Title level={5} style={{ marginBottom: "16px" }}>
        Filter modal for trip verification queue
      </Title>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12}>
          <div>
            <Text strong style={{ display: "block", marginBottom: "8px" }}>
              User Name
            </Text>
            <Input
              size="large"
              placeholder="Enter user name"
              value={filterUserName}
              onChange={(e) => setFilterUserName(e.target.value)}
            />
          </div>
        </Col>
        <Col xs={24} sm={12}>
          <div>
            <Text strong style={{ display: "block", marginBottom: "8px" }}>
              Trip ID
            </Text>
            <Input
              size="large"
              placeholder="Enter trip ID"
              value={filterTripId}
              onChange={(e) => setFilterTripId(e.target.value)}
            />
          </div>
        </Col>
        <Col xs={24} sm={12}>
          <div>
            <Text strong style={{ display: "block", marginBottom: "8px" }}>
              Trip Status
            </Text>
            <Select
              size="large"
              placeholder="Select status"
              value={filterTripStatus || undefined}
              onChange={(value) => setFilterTripStatus(value)}
              style={{ width: "100%" }}
              allowClear
            >
              <Option value="Pending">Pending</Option>
              <Option value="Rejected">Rejected</Option>
              <Option value="Approved">Approved</Option>
              <Option value="Auto Approved">Auto Approved</Option>
              <Option value="In Progress">In Progress</Option>
            </Select>
          </div>
        </Col>
        <Col xs={24} sm={12}>
          <div>
            <Text strong style={{ display: "block", marginBottom: "8px" }}>
              Mode
            </Text>
            <Select
              size="large"
              placeholder="Select mode"
              value={filterMode || undefined}
              onChange={(value) => setFilterMode(value)}
              style={{ width: "100%" }}
              allowClear
              showSearch
            >
              <Option value="walk">Walk</Option>
              <Option value="bicycle">Bicycle</Option>
              <Option value="electricScooter">E-Scooter</Option>
              <Option value="motorcycle">Motorcycle</Option>
              <Option value="train">Train</Option>
              <Option value="metro">Metro</Option>
              <Option value="tram">Tram</Option>
              <Option value="bus">Bus</Option>
              <Option value="coach">Coach</Option>
              <Option value="ferry">Ferry</Option>
              <Option value="car">Car</Option>
              <Option value="rideShare">Ride Share</Option>
              <Option value="transit">Transit</Option>
            </Select>
          </div>
        </Col>
        <Col xs={24}>
          <div>
            <Text strong style={{ display: "block", marginBottom: "8px" }}>
              Date Range
            </Text>
            <RangePicker
              size="large"
              value={filterDateRange}
              onChange={(dates) => setFilterDateRange(dates)}
              style={{ width: "100%" }}
            />
          </div>
        </Col>
      </Row>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: "12px",
          marginTop: "20px",
          paddingTop: "16px",
          borderTop: "1px solid #E5E7EB",
        }}
      >
        <Button size="large" onClick={clearFilters}>
          Clear
        </Button>
        <Button
          type="primary"
          size="large"
          onClick={applyFilters}
          style={{
            backgroundColor: "#5FB92A",
            minWidth: "100px",
          }}
        >
          Apply
        </Button>
      </div>
    </div>
  );

  return (
    // Set the theme for Ant Design components to match the app's green branding
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#5FB92A", // App's Green
          fontFamily: "'Manrope', sans-serif",
          borderRadius: 8,
        },
      }}
    >
      <DashboardLayout>
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <Card bordered={false} style={{ boxShadow: "none" }}>
            {/* Header Section */}
            <Row
              justify="space-between"
              align="middle"
              style={{ marginBottom: 24 }}
            >
              <Col>
                <Title level={3} style={{ margin: 0, fontWeight: 600 }}>
                  Trip Verification Queue
                </Title>
                <Text type="secondary">
                  View your users trips and verify them.
                </Text>
              </Col>
              <Col>
                <Button
                  icon={<DownloadOutlined />}
                  size="large"
                  onClick={handleExport}
                >
                  Export
                </Button>
              </Col>
            </Row>

            {/* Filter Section */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }} align="middle">
              <Col flex="1 1 300px">
                <Input
                  size="large"
                  placeholder="Search by trip ID or user name"
                  prefix={<SearchOutlined style={{ color: "#667085" }} />}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  allowClear
                />
              </Col>
              <Col flex="0 1 auto">
                <Space>
                  <RangePicker
                    size="large"
                    onChange={(dates) => {
                      setDateRange(dates);
                      // Update applied filters to trigger fetch
                      setAppliedFilters((prev) => ({
                        ...prev,
                        dateRange: dates,
                      }));
                    }}
                  />
                  <Dropdown
                    dropdownRender={renderFilterDropdown}
                    trigger={["click"]}
                    open={filterDropdownVisible}
                    onOpenChange={(visible) =>
                      setFilterDropdownVisible(visible)
                    }
                    placement="bottomRight"
                  >
                    <Button icon={<FilterOutlined />} size="large">
                      Filters
                    </Button>
                  </Dropdown>
                </Space>
              </Col>
            </Row>

            {/* Bulk Action Bar */}
            {selectedRowKeys.length > 0 && (
              <Card
                style={{
                  marginBottom: 16,
                  border: "1px solid #E5E7EB",
                  borderRadius: "8px",
                }}
              >
                <Row justify="space-between" align="middle">
                  <Col>
                    <Title level={5} style={{ margin: 0, fontWeight: 600 }}>
                      Review and Take Action on Selected Trips (
                      {selectedRowKeys.length})
                    </Title>
                    <Text type="secondary" style={{ fontSize: "14px" }}>
                      You've selected {selectedRowKeys.length}{" "}
                      {selectedRowKeys.length === 1 ? "trip" : "trips"} for
                      review. Please confirm whether you want to approve or
                      reject them.
                    </Text>
                  </Col>
                  <Col>
                    <Space>
                      <Button
                        danger
                        size="large"
                        onClick={() => handleReject()}
                        style={{ minWidth: "100px" }}
                      >
                        Reject
                      </Button>
                      <Button
                        type="primary"
                        size="large"
                        onClick={() => handleApprove()}
                        style={{
                          minWidth: "100px",
                          backgroundColor: "#5FB92A",
                        }}
                      >
                        Approve
                      </Button>
                    </Space>
                  </Col>
                </Row>
              </Card>
            )}

            {/* Table Section */}
            {error && (
              <Alert
                message="Error"
                description={error}
                type="error"
                showIcon
                closable
                onClose={() => setError(null)}
                style={{ marginBottom: 16 }}
              />
            )}
            <div className="trip-verification-pagination-wrapper">
              {loading && (
                <div className="table-progress-bar">
                  <div className="table-progress-bar-inner" />
                </div>
              )}
                <Table
                  rowSelection={rowSelection}
                  columns={columns}
                  dataSource={data}
                  pagination={{
                    position: ["bottomCenter"],
                    showSizeChanger: false,
                    hideOnSinglePage: true,
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    showPrevNextJumpers: false,
                    onChange: (page, pageSize) => {
                      fetchTrips(page, pageSize);
                    },
                    itemRender: (current, type, originalElement) => {
                      const totalPages = Math.ceil(pagination.total / pagination.pageSize);
                      if (type === "prev") {
                        if (totalPages <= 1) return null;
                        const isFirstPage = pagination.current <= 1;
                        return (
                          <Button
                            className="custom-pagination-link"
                            disabled={isFirstPage}
                          >
                            ← Previous
                          </Button>
                        );
                      }
                      if (type === "next") {
                        if (totalPages <= 1) return null;
                        const isLastPage = pagination.current >= totalPages;
                        return (
                          <Button
                            className="custom-pagination-link"
                            disabled={isLastPage}
                          >
                            Next →
                          </Button>
                        );
                      }
                      return originalElement;
                    },
                  }}
                  scroll={{ x: "max-content" }}
                  style={{ background: "#fff" }}
                  locale={{
                    emptyText: (
                      <div
                        style={{
                          padding: "60px 20px",
                          textAlign: "center",
                        }}
                      >
                        <div
                          style={{
                            width: "120px",
                            height: "120px",
                            margin: "0 auto 24px",
                            backgroundColor: "#F0F9FF",
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <img
                            src={emptyStateImage}
                            alt="No trips"
                            style={{ width: "80px", height: "80px" }}
                          />
                        </div>
                        <Title
                          level={4}
                          style={{ marginBottom: "8px", color: "#1F2937" }}
                        >
                          No trips available at the moment.
                        </Title>
                        <Text type="secondary" style={{ fontSize: "14px" }}>
                          New trips will appear here once users
                          <br />
                          start recording their activity.
                        </Text>
                      </div>
                    ),
                  }}
                />
            </div>
          </Card>
        </Box>
      </DashboardLayout>

      {/* Approve Confirmation Modal */}
      <Modal
        open={approveModalVisible}
        onCancel={() => setApproveModalVisible(false)}
        footer={null}
        centered
        width={400}
        closable={false}
        maskClosable={false}
        destroyOnClose
      >
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <div
            style={{
              width: "48px",
              height: "48px",
              borderRadius: "50%",
              backgroundColor: "#D1FADF",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px",
            }}
          >
            <img
              src={successCheckIcon}
              alt="Success"
              style={{ width: "24px", height: "24px" }}
            />
          </div>
          <Title level={4} style={{ marginBottom: "8px" }}>
            Confirm Trip Approval
          </Title>
          <Text
            type="secondary"
            style={{ display: "block", marginBottom: "24px" }}
          >
            {selectedTrip
              ? "Are you sure you want to approve this trip?"
              : `Are you sure you want to approve ${selectedRowKeys.length} trips?`}
            <br />
            {selectedTrip
              ? "The user will be notified and the trip will be marked as Verified."
              : "The users will be notified and the trips will be marked as Verified."}
          </Text>
          <Space style={{ width: "100%", justifyContent: "center" }}>
            <Button
              onClick={() => setApproveModalVisible(false)}
              style={{ minWidth: "100px" }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={confirmApprove}
              loading={processingAction}
              style={{
                minWidth: "100px",
                backgroundColor: "#5FB92A",
              }}
            >
              Confirm
            </Button>
          </Space>
        </div>
      </Modal>

      {/* Reject Modal with Reasons */}
      <Modal
        open={rejectModalVisible}
        onCancel={() => setRejectModalVisible(false)}
        footer={null}
        centered
        width={420}
        closable={false}
        maskClosable={false}
        destroyOnClose
        bodyStyle={{ padding: "24px" }}
      >
        <div>
          {/* Red circular icon with exclamation mark */}
          <div
            style={{
              width: "48px",
              height: "48px",
              borderRadius: "50%",
              backgroundColor: "#FEE4E2",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: "16px",
            }}
          >
            <img
              src={errorExclamationIcon}
              alt="Error"
              style={{ width: "24px", height: "24px" }}
            />
          </div>

          {/* Title */}
          <Title
            level={5}
            style={{
              marginBottom: "8px",
              fontSize: "18px",
              fontWeight: 600,
              color: "#101828",
              lineHeight: "28px",
            }}
          >
            Reject Trip Verification
          </Title>

          {/* Description */}
          <Text
            style={{
              display: "block",
              marginBottom: "20px",
              fontSize: "14px",
              color: "#667085",
              lineHeight: "20px",
            }}
          >
            {selectedTrip
              ? "Confirm whether you want to reject this ticket upload. Select a reason for rejection so the user can correct and re-upload the ticket."
              : `Confirm whether you want to reject ${selectedRowKeys.length} ticket uploads. Select a reason for rejection so the users can correct and re-upload their tickets.`}
          </Text>

          {/* Radio buttons for rejection reasons */}
          <Radio.Group
            onChange={(e) => setRejectReason(e.target.value)}
            value={rejectReason}
            style={{ width: "100%" }}
          >
            <Space direction="vertical" style={{ width: "100%", gap: "12px" }}>
              <Radio
                value="Blurry or invalid image"
                style={{
                  fontSize: "14px",
                  color: "#344054",
                  fontWeight: 400,
                  lineHeight: "20px",
                }}
              >
                Blurry or invalid image
              </Radio>
              <Radio
                value="Wrong ticket uploaded"
                style={{
                  fontSize: "14px",
                  color: "#344054",
                  fontWeight: 400,
                  lineHeight: "20px",
                }}
              >
                Wrong ticket uploaded
              </Radio>
              <Radio
                value="Missing proof of travel"
                style={{
                  fontSize: "14px",
                  color: "#344054",
                  fontWeight: 400,
                  lineHeight: "20px",
                }}
              >
                Missing proof of travel
              </Radio>
              <Radio
                value="Other"
                style={{
                  fontSize: "14px",
                  color: "#344054",
                  fontWeight: 400,
                  lineHeight: "20px",
                }}
              >
                Other
              </Radio>
            </Space>
          </Radio.Group>

          {/* Text area for "Other" reason */}
          {rejectReason === "Other" && (
            <TextArea
              placeholder="Enter your reason here..."
              value={otherReasonText}
              onChange={(e) => setOtherReasonText(e.target.value)}
              rows={4}
              style={{
                marginTop: "12px",
                borderRadius: "8px",
                fontSize: "14px",
                padding: "10px 14px",
                border: "1px solid #D0D5DD",
                backgroundColor: "#F9FAFB",
              }}
            />
          )}

          {/* Action buttons */}
          <div
            style={{
              display: "flex",
              gap: "12px",
              marginTop: "24px",
            }}
          >
            <Button
              onClick={() => setRejectModalVisible(false)}
              style={{
                flex: 1,
                height: "44px",
                borderRadius: "8px",
                fontSize: "16px",
                fontWeight: 600,
                border: "1px solid #D0D5DD",
                color: "#344054",
                backgroundColor: "#FFFFFF",
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={confirmReject}
              loading={processingAction}
              style={{
                flex: 1,
                height: "44px",
                borderRadius: "8px",
                fontSize: "16px",
                fontWeight: 600,
                backgroundColor: "#D92D20",
                borderColor: "#D92D20",
                boxShadow: "none",
              }}
              className="reject-trip-button"
            >
              Reject
            </Button>
          </div>
        </div>
      </Modal>

      {/* Success Modal */}
      <Modal
        open={successModalVisible}
        onCancel={closeSuccessModal}
        footer={null}
        centered
        width={400}
        closable={false}
        maskClosable={false}
        destroyOnClose
      >
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <div
            style={{
              width: "48px",
              height: "48px",
              borderRadius: "50%",
              backgroundColor: "#D1FADF",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px",
            }}
          >
            <img
              src={successCheckIcon}
              alt="Success"
              style={{ width: "24px", height: "24px" }}
            />
          </div>
          <Title level={4} style={{ marginBottom: "8px" }}>
            {actionCount > 1
              ? `Trips ${actionType === "approve" ? "Approved" : "Rejected"}`
              : `Trip ${actionType === "approve" ? "Approved" : "Rejected"}`}
          </Title>
          <Text
            type="secondary"
            style={{ display: "block", marginBottom: "24px" }}
          >
            {actionCount > 1
              ? `${actionCount} Trips have been successfully ${actionType === "approve" ? "Approved" : "Rejected"
              }.`
              : `This Trip has been successfully ${actionType === "approve" ? "Approved" : "Rejected"
              }.`}
          </Text>
          <Button
            type="primary"
            onClick={closeSuccessModal}
            style={{
              minWidth: "120px",
              backgroundColor: "#5FB92A",
            }}
          >
            Done
          </Button>
        </div>
      </Modal>

      {/* Proof Preview Modal */}
      <Modal
        open={proofModalVisible}
        onCancel={() => {
          setProofModalVisible(false);
          setProofUrls([]);
          setProofIndex(0);
        }}
        footer={null}
        centered
        width={700}
        destroyOnClose
        title={
          <span style={{ fontWeight: 600, fontSize: "16px" }}>
            Proof of Travel
            {proofUrls.length > 1 && (
              <span
                style={{
                  marginLeft: "10px",
                  fontSize: "13px",
                  fontWeight: 400,
                  color: "#667085",
                }}
              >
                ({proofIndex + 1} of {proofUrls.length})
              </span>
            )}
          </span>
        }
        bodyStyle={{ padding: "16px 24px 24px" }}
      >
        {proofUrls.length > 0 && (() => {
          const proof = proofUrls[proofIndex];
          const url = typeof proof === "string" ? proof : proof.url;
          const modeName = typeof proof === "string" ? "" : proof.modeName;
          const ext = url.split("?")[0].split(".").pop().toLowerCase();
          const isImage = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"].includes(ext);
          const isPdf = ext === "pdf";

          return (
            <div>
              {/* Mode of Transport label */}
              {modeName && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "12px",
                  }}
                >
                  <span style={{ color: "#667085", fontSize: "13px" }}>Mode of Transport:</span>
                  <span
                    style={{
                      background: "#F0FDF4",
                      color: "#16A34A",
                      border: "1px solid #BBF7D0",
                      borderRadius: "6px",
                      padding: "2px 10px",
                      fontSize: "13px",
                      fontWeight: 600,
                    }}
                  >
                    {modeName}
                  </span>
                </div>
              )}

              {/* Preview area */}
              <div
                style={{
                  border: "1px solid #E5E7EB",
                  borderRadius: "8px",
                  overflow: "hidden",
                  minHeight: "350px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#F9FAFB",
                  marginBottom: "16px",
                }}
              >
                {isImage ? (
                  <img
                    src={url}
                    alt={`Proof ${proofIndex + 1}`}
                    style={{
                      maxWidth: "100%",
                      maxHeight: "500px",
                      objectFit: "contain",
                      display: "block",
                    }}
                  />
                ) : isPdf ? (
                  <iframe
                    src={url}
                    title={`Proof ${proofIndex + 1}`}
                    style={{ width: "100%", height: "500px", border: "none" }}
                  />
                ) : (
                  <div style={{ textAlign: "center", padding: "40px" }}>
                    <FileImageOutlined
                      style={{ fontSize: "48px", color: "#004EEB", marginBottom: "16px" }}
                    />
                    <div style={{ marginBottom: "12px", color: "#344054", fontWeight: 500 }}>
                      This document cannot be previewed directly.
                    </div>
                    <Button
                      type="primary"
                      onClick={() => window.open(url, "_blank")}
                      style={{ backgroundColor: "#5FB92A", borderColor: "#5FB92A" }}
                    >
                      Open in New Tab
                    </Button>
                  </div>
                )}
              </div>

              {/* Navigation controls */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "12px",
                }}
              >
                <Button
                  disabled={proofIndex === 0}
                  onClick={() => setProofIndex((i) => i - 1)}
                  style={{ minWidth: "100px" }}
                >
                  Previous
                </Button>

                {/* Open external link */}
                <Button
                  icon={<FileImageOutlined />}
                  onClick={() => window.open(url, "_blank")}
                >
                  Open Original
                </Button>

                <Button
                  disabled={proofIndex === proofUrls.length - 1}
                  onClick={() => setProofIndex((i) => i + 1)}
                  style={{ minWidth: "100px" }}
                >
                  Next
                </Button>
              </div>
            </div>
          );
        })()}
      </Modal>
    </ConfigProvider>
  );
};

export default TripVerification;
